<script setup lang="ts">
import { useFileManagerCreateFolder } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerCreateFolder';
import { useFileManagerNavigation } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerNavigation';
import type { FileExplorerIconSize } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerSettings';
import { useFileManagerUpload } from '@/components/document/sub-components/file-manager/logic/upload/useFileManagerUpload';
import {
  computeCreateFolderMode,
  computeUploadMode,
} from '@/components/document/sub-components/file-manager/permissions/modes';
import { useFileManagerPermissions } from '@/components/document/sub-components/file-manager/permissions/useFileManagerPermissions';
// Aliased, and it must stay aliased: this component already has an `isAtRoot` PROP (about
// currentPath, for the back button). In <script setup> an import of the same name shadows the prop
// for the whole template, which would silently turn `:disabled="isAtRoot"` into a function reference
// — always truthy, back button dead forever. The two also mean different things here: the prop is
// about where the user is, this predicate is about where an upload landed.
import {
  getPathName,
  isAtRoot as isRootPath,
} from '@/components/document/sub-components/file-manager/utils';
import {
  PhCaretDown,
  PhCheckCircle,
  PhFile,
  PhFolder,
  PhFolderPlus,
  PhGear,
  PhMagnifyingGlass,
  PhPlus,
  PhSpinnerGap,
  PhSquare,
  PhWarningCircle,
  PhX,
} from '@phosphor-icons/vue';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { BackButton } from './sub-components/back-button';
import {
  IconSizeToggle,
  type IconSizeToggleOption,
} from './sub-components/icon-size-toggle';

const { t } = useI18n();

defineProps<{
  isAtRoot: boolean;
}>();

const emit = defineEmits<{
  back: [];
}>();

const iconSize = defineModel<FileExplorerIconSize>('iconSize', {
  required: true,
});
const searchQuery = defineModel<string>('searchQuery', { required: true });

const upload = useFileManagerUpload();
const createFolderState = useFileManagerCreateFolder();
const {
  isUploading,
  canUpload,
  uploadItems,
  uploadDestinationPath,
  dismissUploadItems,
} = upload;
const { isPending: isCreatingFolderPending, canCreateFolder } =
  createFolderState;

// Scoped to the folder the toolbar is acting on — its buttons all target currentPath, and with
// capabilities per mount that is the only path whose permissions they may consult.
const { currentPath } = useFileManagerNavigation();
const { permissionsAt } = useFileManagerPermissions();
const createFolderMode = computed(() =>
  computeCreateFolderMode(permissionsAt(currentPath.value)),
);
const uploadMode = computed(() =>
  computeUploadMode(permissionsAt(currentPath.value)),
);
// Folder upload genuinely creates folders, so - unlike plain file upload - it needs both
// capability strings rather than letting files:upload alone bypass the createFolder gate.
const folderUploadMode = computed(() =>
  createFolderMode.value === 'visible' && uploadMode.value === 'visible'
    ? 'visible'
    : 'hidden',
);

// Names where the running batch is landing. NOT derivable from currentPath — and not the `isAtRoot`
// prop above either, which is about currentPath: a drop on the sidebar tree can target a folder in
// another branch or another mount entirely, with the main pane sitting somewhere unrelated.
//
// Shows the destination's own name (its last segment), which is the label the user just dropped onto,
// with the full virtual path as a `title`: two folders can share a name across branches or projects,
// and the panel is only 16rem wide, so the full path inline would truncate away the very part that
// identifies the folder.
//
// At the root there is no last segment, so this falls back to the plain heading rather than inventing
// a name for it — the same call already made for the grid's background context-menu header. Not
// reachable from the tree (the tree has no root row), but the pickers can target '/' when a host
// grafts its only mount there.
const uploadsHeading = computed(() => {
  const destination = uploadDestinationPath.value;
  if (destination === null || isRootPath(destination)) {
    return t('fileManager.fileExplorerView.fileExplorerHandler.uploadsHeading');
  }
  return t('fileManager.fileExplorerView.fileExplorerHandler.uploadsHeadingTo', {
    folder: getPathName(destination),
  });
});

const uploadsHeadingTitle = computed(() =>
  uploadDestinationPath.value && !isRootPath(uploadDestinationPath.value)
    ? uploadDestinationPath.value
    : undefined,
);

const newMenuRef = ref<HTMLElement | null>(null);
const isNewMenuOpen = ref(false);

const toggleNewMenu = () => {
  isNewMenuOpen.value = !isNewMenuOpen.value;
};

// Below the `file-explorer-handler` container-query breakpoint, the size toggle collapses into
// this cog button/popover. It's the only setting today, so opening it goes straight to the size
// picker — once more settings exist, this becomes a menu with the size picker as one section.
const settingsMenuRef = ref<HTMLElement | null>(null);
const isSettingsMenuOpen = ref(false);

const toggleSettingsMenu = () => {
  isSettingsMenuOpen.value = !isSettingsMenuOpen.value;
};

// Picking a size dismisses the popover, the way the create menu's own entries do — the click-outside
// handler can't do it, the toggle being inside settingsMenuRef. Only the popover's copy of the toggle
// closes anything; the always-visible one in the toolbar has no popover to close, so it keeps writing
// iconSize directly.
const selectIconSizeFromSettings = (value: FileExplorerIconSize) => {
  iconSize.value = value;
  isSettingsMenuOpen.value = false;
};

const handleClickOutside = (event: MouseEvent) => {
  if (!newMenuRef.value?.contains(event.target as Node)) {
    isNewMenuOpen.value = false;
  }
  if (!settingsMenuRef.value?.contains(event.target as Node)) {
    isSettingsMenuOpen.value = false;
  }
};

onMounted(() => window.addEventListener('click', handleClickOutside));
onUnmounted(() => window.removeEventListener('click', handleClickOutside));

const selectNewFile = () => {
  isNewMenuOpen.value = false;
  upload.openFilePicker();
};

const selectNewFolderUpload = () => {
  isNewMenuOpen.value = false;
  upload.openFolderPicker();
};

const selectNewDirectory = () => {
  isNewMenuOpen.value = false;
  createFolderState.openModal();
};

const sizeOptions = computed<IconSizeToggleOption[]>(() => [
  { value: 'small', text: t('fileManager.fileExplorerView.fileExplorerHandler.sizeSmall'), icon: PhSquare, iconSize: 10 },
  { value: 'medium', text: t('fileManager.fileExplorerView.fileExplorerHandler.sizeMedium'), icon: PhSquare, iconSize: 13 },
  { value: 'large', text: t('fileManager.fileExplorerView.fileExplorerHandler.sizeLarge'), icon: PhSquare, iconSize: 16 },
]);
</script>

<template>
  <div class="file-explorer-handler">
    <div class="search-area">
      <BackButton :disabled="isAtRoot" @click="emit('back')" />
      <div class="search-box">
        <PhMagnifyingGlass :size="14" class="search-icon" />
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          :placeholder="t('fileManager.fileExplorerView.fileExplorerHandler.searchPlaceholder')"
        />
        <span class="search-clear-slot">
          <button
            v-if="searchQuery"
            type="button"
            class="search-clear"
            @click="searchQuery = ''"
          >
            <PhX :size="12" weight="bold" />
          </button>
        </span>
      </div>
    </div>
    <div class="file-explorer-handler-actions">
      <IconSizeToggle
        class="size-toggle-full"
        :options="sizeOptions"
        :model-value="iconSize"
        @update:model-value="
          (value) => (iconSize = value as FileExplorerIconSize)
        "
      />
      <div ref="settingsMenuRef" class="settings-menu">
        <button
          type="button"
          class="settings-button"
          :class="{ 'is-open': isSettingsMenuOpen }"
          :aria-label="t('fileManager.fileExplorerView.fileExplorerHandler.viewSettingsAriaLabel')"
          @click.stop="toggleSettingsMenu"
        >
          <PhGear :size="18" weight="fill" />
        </button>
        <div v-if="isSettingsMenuOpen" class="settings-menu-dropdown">
          <div class="settings-menu-section">
            <span class="settings-menu-label">{{ t('fileManager.fileExplorerView.fileExplorerHandler.sizeSectionLabel') }}</span>
            <IconSizeToggle
              :options="sizeOptions"
              :model-value="iconSize"
              @update:model-value="
                (value) =>
                  selectIconSizeFromSettings(value as FileExplorerIconSize)
              "
            />
          </div>
        </div>
      </div>
      <div
        v-if="createFolderMode === 'visible' || uploadMode === 'visible'"
        ref="newMenuRef"
        class="new-menu"
      >
        <button
          type="button"
          class="new-button"
          :aria-label="t('fileManager.fileExplorerView.fileExplorerHandler.createAriaLabel')"
          :disabled="isUploading || isCreatingFolderPending"
          @click.stop="toggleNewMenu"
        >
          <PhPlus :size="16" weight="bold" />
          <span class="new-button-label">{{ t('fileManager.fileExplorerView.fileExplorerHandler.createButtonLabel') }}</span>
          <span class="new-button-divider" />
          <PhCaretDown
            :size="12"
            weight="bold"
            class="new-button-caret"
            :class="{ 'is-open': isNewMenuOpen }"
          />
        </button>
        <div v-if="isNewMenuOpen" class="new-menu-dropdown">
          <button
            v-if="createFolderMode === 'visible'"
            type="button"
            class="new-menu-item"
            :disabled="!canCreateFolder"
            @click="selectNewDirectory"
          >
            <PhFolder :size="16" />
            <span>{{ t('fileManager.fileExplorerView.fileExplorerHandler.createDirectoryMenuItem') }}</span>
          </button>
          <button
            v-if="uploadMode === 'visible'"
            type="button"
            class="new-menu-item"
            :disabled="!canUpload"
            @click="selectNewFile"
          >
            <PhFile :size="16" />
            <span>{{ t('fileManager.fileExplorerView.fileExplorerHandler.createFileMenuItem') }}</span>
          </button>
          <button
            v-if="folderUploadMode === 'visible'"
            type="button"
            class="new-menu-item"
            :disabled="!canUpload"
            @click="selectNewFolderUpload"
          >
            <PhFolderPlus :size="16" />
            <span>{{ t('fileManager.fileExplorerView.fileExplorerHandler.uploadFolderMenuItem') }}</span>
          </button>
        </div>
      </div>
    </div>
    <div v-if="uploadItems.length" class="upload-progress-panel">
      <div class="upload-progress-header">
        <span class="upload-progress-heading" :title="uploadsHeadingTitle">
          {{ uploadsHeading }}
        </span>
        <button
          type="button"
          class="upload-progress-close"
          :aria-label="t('fileManager.fileExplorerView.fileExplorerHandler.dismissUploadProgressAriaLabel')"
          @click="dismissUploadItems"
        >
          <PhX :size="12" weight="bold" />
        </button>
      </div>
      <div
        v-for="item in uploadItems"
        :key="item.relativePath"
        class="upload-progress-item"
      >
        <PhCheckCircle
          v-if="item.status === 'done'"
          :size="14"
          class="status-done"
        />
        <PhWarningCircle
          v-else-if="item.status === 'failed'"
          :size="14"
          class="status-failed"
        />
        <PhSpinnerGap v-else :size="14" class="status-pending" />
        <span class="upload-progress-name">{{ item.relativePath }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.file-explorer-handler {
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  /* A floor under justify-content's own leftover-space gap: once the free space between the search
     box and the actions row shrinks to this, flexbox holds the gap here (rather than letting it
     keep shrinking toward 0) and pushes the remaining compression onto the search box instead —
     matches the fixed gap already used between the cog and create button. */
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  /* Lets the actions row react to this bar's own width (e.g. a resized sidebar/pane) rather than
     the viewport's, so the size toggle collapses to the cog button whenever there isn't room for
     it here specifically. */
  container-type: inline-size;
  container-name: file-explorer-handler;
  /* The create ("Skapa") button's own natural height is the reference every other control in this
     row — search box, size toggle, cog — is sized to match. Declared here (rather than on
     .file-explorer-handler-actions) so the search box, its sibling, can read it too; custom
     properties inherit through the DOM regardless of Vue's scoped-style attribute boundaries. */
  --handler-control-height: 1.875rem;
}

.search-area {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  /* Groups the back button with the search box so justify-content: space-between on the parent
     still only spreads two things apart (this group vs. the actions on the right) instead of
     spacing the back button away from search. */
  min-width: 0;
}

.file-explorer-handler-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  /* Never compresses — once the gap above hits its floor, the search box is the only thing left
     that can give way. */
  flex-shrink: 0;
}

.settings-menu {
  position: relative;
  display: none;
}

.settings-button {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: var(--handler-control-height);
  height: var(--handler-control-height);
  background-color: #f5f5f7;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #6b7280;
}

.settings-menu-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.25rem;
  width: max-content;
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  padding: 0.75rem;
  z-index: 10;
}

.settings-menu-section {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.settings-menu-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
}

/* Below this width, the always-visible size toggle no longer fits comfortably alongside search
   and create — it collapses into the cog button/popover instead. */
@container file-explorer-handler (max-width: 520px) {
  .size-toggle-full {
    display: none;
  }

  .settings-menu {
    display: block;
  }
}

.search-box {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  height: var(--handler-control-height);
  /* Floor for the shrink below — .search-input (the only unshrunk-flex-shrink:0 child) gives up
     its width down to this point before the box itself would need to compress further. */
  min-width: 6rem;
  border: none;
  border-radius: 999px;
  padding: 0 0.75rem;
  background-color: #f0f0f2;
}

.search-icon {
  color: #999;
  flex-shrink: 0;
}

.search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  font-family: inherit;
  width: 12rem;
  min-width: 0;
}

.search-input::placeholder {
  color: #bbb;
}

.search-clear-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
}

.search-clear {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0.125rem;
  color: #999;
}

.search-clear:hover {
  color: #444;
}

.new-menu {
  position: relative;
  margin-left: 0.5rem;
}

/* The cog reads as one compact control with create, rather than two separated ones — drop the
   extra gap the full-width size toggle needed to stay visually distinct from it. Declared after
   the base .new-menu rule above so it actually wins the cascade (equal specificity, later wins). */
@container file-explorer-handler (max-width: 520px) {
  .new-menu {
    margin-left: 0;
  }
}

.new-button {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  height: var(--handler-control-height);
  background-color: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: 999px;
  cursor: pointer;
  font-size: 1rem;
  color: #fff;
  padding: 0 0.875rem;
}

.new-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.new-button-label {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  line-height: 1;
}

.new-button-divider {
  align-self: stretch;
  width: 1px;
  margin-left: 0.375rem;
  background-color: #fff;
  opacity: 0.4;
}

.new-button-caret {
  transition: transform 0.15s ease;
}

.new-button-caret.is-open {
  transform: rotate(180deg);
}

/* Narrower than the size-toggle collapse breakpoint: the button drops its label, divider, and
   caret, becoming a plain circular plus button (aria-label keeps it accessible). */
@container file-explorer-handler (max-width: 460px) {
  .new-button-label,
  .new-button-divider,
  .new-button-caret {
    display: none;
  }

  .new-button {
    width: var(--handler-control-height);
    justify-content: center;
    padding: 0;
  }
}

.new-menu-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.25rem;
  width: max-content;
  min-width: 8rem;
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  z-index: 10;
}

.new-menu-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  white-space: nowrap;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
}

.new-menu-item:hover {
  background-color: #f5f5f7;
}

.new-menu-item:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.upload-progress-panel {
  position: absolute;
  top: 100%;
  right: 1rem;
  margin-top: 0.25rem;
  max-height: 12rem;
  width: 16rem;
  overflow-y: auto;
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 10;
}

.upload-progress-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-bottom: 1px solid #eee;
}

/* The heading now carries a folder name, so it can outgrow the 16rem panel — it is the part allowed
   to ellipsize (the full path is on its `title` anyway), never the dismiss button beside it. */
.upload-progress-heading {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-progress-close {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.125rem;
  color: #999;
}

.upload-progress-close:hover {
  color: #444;
}

.upload-progress-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
}

.upload-progress-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-done {
  color: #2e9e5b;
  flex-shrink: 0;
}

.status-failed {
  color: #d1453b;
  flex-shrink: 0;
}

.status-pending {
  color: #999;
  flex-shrink: 0;
  animation: upload-progress-spin 1s linear infinite;
}

@keyframes upload-progress-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
